import { z } from 'zod';

/**
 * School logos may be square (crest, 1:1) or wide (wordmark, up to 4:1). tenant-api reads the
 * intrinsic size at upload, the manifest carries the aspect and shape, the app lays them out.
 */

/** width / height at or above which a logo is laid out as a wide wordmark. */
export const LOGO_WIDE_MIN_ASPECT = 1.6;
/** Widest logo accepted at upload (4:1). */
export const LOGO_MAX_ASPECT = 4;
/** Tallest logo accepted at upload (1:4); tall logos are laid out like square ones. */
export const LOGO_MIN_ASPECT = 1 / LOGO_MAX_ASPECT;
/** Smallest PNG side accepted at upload, in pixels. */
export const LOGO_MIN_PNG_SIDE = 16;

export const logoShapeSchema = z.enum(['square', 'wide']);
export type LogoShape = z.infer<typeof logoShapeSchema>;

const ASPECT_DECIMALS = 10_000;

/** width / height rounded to 4 decimals; null when either side is not a positive number. */
export function logoAspectOf(width: number, height: number): number | null {
  if (!(width > 0 && height > 0) || !Number.isFinite(width) || !Number.isFinite(height)) {
    return null;
  }
  return Math.round((width / height) * ASPECT_DECIMALS) / ASPECT_DECIMALS;
}

/** Wide from LOGO_WIDE_MIN_ASPECT up; everything else (unknown, 1:1, tall) is square. */
export function logoShapeFor(aspect: number | null | undefined): LogoShape {
  return typeof aspect === 'number' && Number.isFinite(aspect) && aspect >= LOGO_WIDE_MIN_ASPECT
    ? 'wide'
    : 'square';
}

export interface ManifestLogoFields {
  logoAspect?: number;
  logoShape?: LogoShape;
}

/** Manifest fields for a stored logo size; empty when the size is unknown. */
export function manifestLogoFields(
  width: number | null,
  height: number | null,
): ManifestLogoFields {
  const aspect = width === null || height === null ? null : logoAspectOf(width, height);
  return aspect === null ? {} : { logoAspect: aspect, logoShape: logoShapeFor(aspect) };
}

/**
 * Manifest `tenant.logoAspect` / `tenant.logoShape` (additive, apiVersion 1). Lenient on
 * purpose: a value this app version doesn't understand is dropped, never a config error.
 */
export const manifestLogoAspectSchema = z.number().positive().finite().optional().catch(undefined);
export const manifestLogoShapeSchema = logoShapeSchema.optional().catch(undefined);
